import { useState } from "react";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  IconButton,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiCheck, FiCopy, FiLock, FiUnlock } from "react-icons/fi";
import { Field } from "../ui/field";
import { PasswordInput } from "../ui/password-input";
import API from "../../Utils/api";

export const ViewPasswordModal = ({ isOpen, onClose, user }) => {
  const [adminPass, setAdminPass] = useState("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  const isRevealed = revealedPassword !== null;
  const displayName = [user?.fname, user?.lname].filter(Boolean).join(" ") || user?.user_name;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!adminPass) {
      setError("Admin password is required.");
      return;
    }

    setIsVerifying(true);
    try {
      // Called directly instead of via a saga so neither password ends up in the Redux store/devtools.
      const { data } = await API.post("/reveal-user-password", { userId: user?._id, adminPass });
      if (data?.meta?.code === 200) {
        setRevealedPassword(data?.data?.passwordText ?? "");
        setAdminPass("");
      } else {
        setError(data?.meta?.message || "Verification failed.");
      }
    } catch (err) {
      setError(err?.response?.data?.meta?.message || "Something went wrong. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(revealedPassword || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size="sm"
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <HStack gap={3} align="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxSize="40px"
                  borderRadius="full"
                  bg={isRevealed ? "green.50" : "blue.50"}
                  color={isRevealed ? "green.600" : "blue.600"}
                  flexShrink={0}
                >
                  {isRevealed ? <FiUnlock size={18} /> : <FiLock size={18} />}
                </Box>
                <VStack align="flex-start" gap={0}>
                  <Dialog.Title fontSize="md">
                    {isRevealed ? "User Password" : "Admin Verification"}
                  </Dialog.Title>
                  <Text fontSize="xs" color="gray.500">
                    {[displayName, user?.email].filter(Boolean).join(" · ")}
                  </Text>
                </VStack>
              </HStack>
            </Dialog.Header>

            <Dialog.Body>
              {isRevealed ? (
                <VStack align="stretch" gap={2}>
                  <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Password
                  </Text>
                  <HStack
                    justifyContent="space-between"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    pl={3}
                    pr={1}
                    py={1}
                  >
                    <Text fontFamily="mono" fontSize="md" wordBreak="break-all">
                      {revealedPassword || "—"}
                    </Text>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      colorPalette={copied ? "green" : "gray"}
                      onClick={handleCopy}
                      disabled={!revealedPassword}
                      aria-label="Copy password"
                    >
                      {copied ? <FiCheck /> : <FiCopy />}
                    </IconButton>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Keep this password confidential and share it only with the account owner.
                  </Text>
                </VStack>
              ) : (
                <form id="verify-admin-password-form" onSubmit={handleVerify}>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    Enter your admin password to view this user's password.
                  </Text>
                  <Field label="Admin Password" invalid={!!error} errorText={error}>
                    <PasswordInput
                      value={adminPass}
                      onChange={(e) => {
                        setAdminPass(e.target.value);
                        setError("");
                      }}
                      placeholder="Enter admin password"
                      autoComplete="current-password"
                    />
                  </Field>
                </form>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>
                {isRevealed ? "Close" : "Cancel"}
              </Button>
              {isRevealed ? (
                <Button onClick={handleCopy} disabled={!revealedPassword}>
                  {copied ? <FiCheck /> : <FiCopy />}
                  {copied ? "Copied" : "Copy Password"}
                </Button>
              ) : (
                <Button
                  type="submit"
                  form="verify-admin-password-form"
                  loading={isVerifying}
                  loadingText="Verifying"
                  disabled={!adminPass}
                >
                  Verify
                </Button>
              )}
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
